import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@speedoc/design-assets/build/icons";
import { Circle } from "@speedoc/design-assets/build/icons";
import ContainerCard from "./containerCard";
import { SimpleTabs } from "./tabs";
import { useMediaQuery } from "react-responsive";
import { Button } from "@speedoc/design-assets";
import SectionHeading from "./sectionHeading";

const ExpandedCardButtons = ({ content, onClick, mobile }) => {
  const scrollToForm = () => {
    let form;
    try {
      form = document.querySelector("#contactFormAnchor");
      const topPoint = form.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ behavior: "smooth", left: 0, top: topPoint });
    } catch {
      return;
    }
  };

  return (
    <div className="cardExpandedBottomButtons">
      <div className="buttonsContainer">
        <div className="buttons">
          <div className={`linkPrimary ${mobile ? "" : "sm"}`} arrow="right" onClick={scrollToForm}>
            <span>{mobile ? content?.expandedButtonDetailsTitleMobile : content?.expandedButtonDetailsTitle}</span>
          </div>
          {content?.linkToExploreTitle && (
            <Link to={content.linkToExploreUrl}>
              <Button className="secondary sm">
                <span>{content.linkToExploreTitle}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>
      {!mobile && (
        <div className="collapseContainer" onClick={onClick}>
          <ArrowLeft className="arrow left" />
          <span>{content?.expandedCollapse}</span>
        </div>
      )}
    </div>
  );
};

const ExpandedCardBottomInfo = ({ content, services }) => {
  return (
    <div className="expandedCardBottomInfo">
      <div className="priceInfo">
        {(content?.priceBox || []).map((box, i) => (
          <div key={i} className="box">
            <span>{box.currency}</span>
            <span className="amount"> {box.price}</span>
            <span className="info"> {box.info}</span>
            {i < content.priceBox.length - 1 && <span> /&nbsp;</span>}
          </div>
        ))}
      </div>
      <div className="packageDetailsList">
        <p>{services && services[0].text}</p>
        <div className="horizontalList">
          {services &&
            services.slice(1, -1).map(item => {
              return <p key={item.text}>{item.text}</p>;
            })}
        </div>
        <p>{services && services.length > 1 && services[services.length - 1].text}</p>
      </div>
    </div>
  );
};

const checkServiceSize = content => {
  return content.filter(service => service.title.length > 80).length > 0;
};

const PackageDetails = ({ content, fade }) => {
  let fontSizeClassLongContent = content.length > 12 ? "xss" : "";
  return (
    <div className={`packageDetails ${fade ? "fadeIn" : ""} ${checkServiceSize(content) ? "longString" : ""}`}>
      {content.map(service => {
        return (
          <div key={service.title} className="serviceListItem">
            <div className="serviceName">
              <Circle /> <p className={fontSizeClassLongContent}>{service.title}</p>
            </div>
            {service.entries ? (
              <div className="serviceDetails">
                {service.entries.map(entry => {
                  return (
                    <p className={fontSizeClassLongContent} key={entry.text}>
                      {entry.text}
                    </p>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const ExpandedCard = ({ content, onClick, style, className }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [fade, setFade] = useState(true);

  const fadeContent = value => {
    return new Promise(resolve => {
      setTimeout(() => {
        setFade(true);
        resolve();
      }, 500);
    });
  };

  const changeContent = i => {
    setActiveTab(i);
    return new Promise(resolve => {
      setTimeout(resolve, 100);
    });
  };

  const switchTab = async i => {
    setFade(false);
    if (activeTab === i) return;
    await fadeContent(true);
    await changeContent(i);
  };

  const buttonDetails = content.tabPackageCards.map((item, index) => {
    return {
      text: item.tabName,
      classes: `secondary sm ${activeTab === index ? "active" : ""} ${
        content.tabPackageCards.length === 1 ? "uniqueTab" : ""
      }`
    };
  });

  return (
    <ContainerCard style={style} className={`fullCard ${className}`} onClickOutside={onClick}>
      <h1 className="title">{content.previewTitle}</h1>
      <div className="cardContent">
        <SimpleTabs buttonDetails={buttonDetails} onClick={i => switchTab(i)} />
        <PackageDetails content={content.tabPackageCards[activeTab]?.expandedList || []} fade={fade} />
        <div className="cardBottom">
          <ExpandedCardBottomInfo
            content={content.tabPackageCards[activeTab]}
            services={content.tabPackageCards[activeTab]?.expandedIncludesList}
          />
          <div className="cardFooter">
            <ExpandedCardButtons content={content.tabPackageCards[activeTab]} onClick={onClick} />
          </div>
        </div>
      </div>
      {content.expandedImage && (
        <img className="cardImage" src={content.expandedImage?.url} alt={content.expandedTitle} />
      )}
    </ContainerCard>
  );
};

const PreviewCard = ({ content, onClick, className }) => {
  return (
    <ContainerCard className={className}>
      <h2 className="title">{content.previewTitle}</h2>
      <div className="cardBody">
        <p className="priceInfo">
          {content.previewCurrency}
          <span className="amount">&nbsp;{content.previewPrice}</span>
        </p>
        <div className="servicesListSmall">
          {content.previewIncludesList.map(item => (
            <p key={item.text}>{item.text}</p>
          ))}
        </div>
        <div className="cardButtons">
          <Button onClick={onClick} type="button" className="secondary sm">
            <span>{content.previewButtonTitle}</span>
          </Button>
          <Link to="/" className="buttonLink link sm" arrow="right">
            <span>{content.previewLinkTitle}</span>
          </Link>
        </div>
      </div>
      {content.previewImage && <img className="cardImage" src={content.previewImage?.url} alt={content.previewTitle} />}
    </ContainerCard>
  );
};

const MobileCard = ({ content, className, handleClick, index, open }) => {
  const [displayBody, setDisplayBody] = useState(false);
  const [hidePreview, setHidePreview] = useState(false);
  const [fadeBody, setFadeBody] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [fade, setFade] = useState(true);

  const fadeContent = value => {
    return new Promise(resolve => {
      setTimeout(() => {
        setFade(true);
        resolve();
      }, 200);
    });
  };

  const changeContent = i => {
    setActiveTab(i);
    return new Promise(resolve => {
      setTimeout(resolve, 100);
    });
  };

  let dataAttrs = {
    "data-card": `card-${index}`
  };

  const buttonDetails = content.tabPackageCards.map((item, itemIndex) => {
    return {
      text: item.tabName,
      classes: `secondary sm ${activeTab === itemIndex ? "active" : ""} ${
        content.tabPackageCards.length === 1 ? "uniqueTab" : ""
      }`
    };
  });

  const switchTab = async i => {
    setFade(false);
    if (activeTab === i) return;
    await fadeContent(true);
    await changeContent(i);
  };

  const closeCard = e => {
    setFadeBody(prevState => !prevState);
    setTimeout(() => {
      showBody(350).then(() => {
        handleClick(e, index);
      });
    }, 200);
  };

  const showBody = useCallback((delay = 900) => {
    setHidePreview(prevState => !prevState);
    return new Promise(resolve => {
      setTimeout(() => {
        setDisplayBody(prevState => !prevState);
        resolve();
      }, delay);
    });
  }, []);

  useEffect(() => {
    if (open) {
      showBody().then(() =>
        setTimeout(() => {
          setFadeBody(prevState => !prevState);
        }, 200)
      );
    }
  }, [open, showBody]);

  return (
    <ContainerCard dataAttrs={dataAttrs} className={`mobileCard ${className}  ${open ? "expand" : ""}`}>
      <h2 className="title">{content.previewTitle}</h2>
      {displayBody ? (
        <div className={`cardBody ${fadeBody ? "display" : ""}`}>
          <SimpleTabs buttonDetails={buttonDetails} onClick={i => switchTab(i)} />
          <PackageDetails content={content.tabPackageCards[activeTab]?.expandedList || []} fade={fade} />
          <ExpandedCardBottomInfo
            content={content.tabPackageCards[activeTab]}
            services={content.tabPackageCards[activeTab]?.expandedIncludesList}
          />
          <ExpandedCardButtons content={content.tabPackageCards[activeTab]} onClick={closeCard} mobile={true} />
        </div>
      ) : (
        <div className={`cardPreview ${hidePreview ? "hide" : ""}`}>
          <div className="cardDetails">
            <p className="priceInfo">
              {content.previewCurrency}
              <span className="amount"> {content.previewPrice}</span>
            </p>
          </div>
          <div className="servicesListSmall">
            {content.tabPackageCards[activeTab]?.expandedIncludesList?.map(item => (
              <p key={item.text}>{item.text}</p>
            ))}
          </div>
          <div>
            <Button onClick={handleClick} type="button" className="secondary sm">
              <span>{content.previewButtonTitle}</span>
            </Button>
          </div>
        </div>
      )}
      {content.expandedImage?.url && (
        <img className="cardImage" src={content.expandedImage?.url} alt={content.expandedTitle} />
      )}
    </ContainerCard>
  );
};

const PackageModules = ({ content }) => {
  const [activeTab] = useState("Basic");
  const [blockClick, setBlockClick] = useState(false);
  const [expandCardIndex, setExpandCardIndex] = useState();
  const [movePreviewCard, setMovePreviewCard] = useState(undefined);
  const [displayFullCard, setDisplayFullCard] = useState(false);
  const [expandFullCard, setExpandFullCard] = useState(false);
  const [showCardContent, setShowCardContent] = useState(false);
  const [fullContainerStyle, setFullContainerStyle] = useState({});
  const [fullCardStyle, setFullCardStyle] = useState({});
  const previewContainer = useRef();

  const injectCard = (delay = 300) => {
    return new Promise(resolve => {
      setDisplayFullCard(!displayFullCard);
      setTimeout(resolve, delay);
    });
  };

  const expandCard = (delay = 400) => {
    return new Promise(resolve => {
      setMovePreviewCard(undefined);
      setExpandFullCard(!expandFullCard);
      setTimeout(resolve, delay);
    });
  };

  const showContent = (delay = 800) => {
    return new Promise(resolve => {
      setShowCardContent(!showCardContent);
      setTimeout(resolve, delay);
    });
  };

  const closeCard = () => {
    if (blockClick) return;
    setBlockClick(true);
    showContent(450)
      .then(() => expandCard())
      .then(() => {
        injectCard(100);
      })
      .then(() =>
        setTimeout(() => {
          setExpandCardIndex(undefined);
          setBlockClick(false);
        }, 100)
      );
  };

  const startAnimation = (e, index) => {
    if (blockClick) return;
    setBlockClick(true);
    setExpandCardIndex(index);
    setMovePreviewCard(index);
    const targetCard = e.currentTarget.parentElement.parentElement.parentElement;
    setFullCardStyle({
      position: "relative",
      width: targetCard.offsetWidth,
      height: targetCard.offsetHeight
    });
    setFullContainerStyle({
      paddingTop: targetCard.offsetTop,
      paddingBottom: index < 2 ? previewContainer.current.offsetHeight - targetCard.offsetHeight : 0,
      paddingLeft: targetCard.offsetLeft,
      paddingRight: index % 2 === 0 ? previewContainer.current.offsetWidth - targetCard.offsetWidth : 0,
      justifyContent: index % 2 === 1 ? "flex-end" : "flex-start",
      alignItems: index > 1 ? "flex-end" : "flex-start"
    });
    setTimeout(() => {
      injectCard(10)
        .then(expandCard)
        .then(() => showContent(250))
        .then(() => setBlockClick(false));
    }, 700);
  };

  const smallCardTransition = index => (movePreviewCard === index ? `translate-${index}` : "");
  return (
    <div id="packageModules">
      <div className="previewCardsWrapper">
        <SectionHeading title={content.sectionTitle} />
        <div className="tabsContainer">
          {content.tabs.map(item => (
            <p key={item.title} className={item.title === activeTab ? "active" : ""}>
              {item.title}
            </p>
          ))}
        </div>

        <div ref={previewContainer} className="previewCardsContainer">
          {content.packageCards.map((cardContent, index) => (
            <PreviewCard
              key={index}
              className={`smallCard ${expandCardIndex === index ? "fadeContent" : ""}  ${
                movePreviewCard !== undefined ? smallCardTransition(index) : ""
              }`}
              content={cardContent}
              onClick={e => startAnimation(e, index)}
            />
          ))}
          {displayFullCard ? (
            <div className={`expandedCardContainer ${expandFullCard ? "expand" : ""}`} style={fullContainerStyle}>
              <ExpandedCard
                className={`offset-${movePreviewCard} ${showCardContent ? "" : "transparent"}`}
                style={fullCardStyle}
                content={content.packageCards[expandCardIndex]}
                onClick={closeCard}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className="bottomLine" dangerouslySetInnerHTML={{ __html: content.bottomLine }} />
      {content.buttonText && (
        <div className="bottomButton button">
          <a href={content.buttonLink} className="linkPrimary sm">
            <div dangerouslySetInnerHTML={{ __html: content.buttonText }} />
          </a>
        </div>
      )}
    </div>
  );
};

const PackageModulesMobile = ({ content }) => {
  const [activeTab] = useState("Basic");
  const [openCardIndex, setOpenCardIndex] = useState(undefined);
  const container = useRef();

  const isSafari = () => {
    return (
      navigator.vendor &&
      navigator.vendor.indexOf("Apple") > -1 &&
      navigator.userAgent &&
      navigator.userAgent.indexOf("CriOS") === -1 &&
      navigator.userAgent.indexOf("FxiOS") === -1
    );
  };

  function scrollIosScreen(position, time = 250) {
    return new Promise(resolve => {
      var scrollY = position / 50;
      var curTime = 0;
      while (curTime <= time) {
        window.setTimeout(() => {
          window.scrollBy({ left: 0, top: scrollY });
        }, curTime);
        curTime += time / 50;
      }
      resolve();
    });
  }

  const scrollScreen = position => {
    window.scrollBy(0, position);
    return new Promise(resolve => {
      setTimeout(resolve, 300);
    });
  };

  const updateIndexes = index => {
    setOpenCardIndex(index);
    return new Promise(resolve => {
      setTimeout(resolve, 300);
    });
  };

  const safariScrollPosition = position => {
    const navBarHeight = document.querySelector("#navigationMobile").offsetHeight;
    if (position < 1) {
      return -(-position + navBarHeight);
    } else if (position <= navBarHeight) {
      return -(position + navBarHeight);
    } else {
      return position - navBarHeight;
    }
  };

  const openCard = async (position, index) => {
    if (isSafari()) {
      scrollIosScreen(safariScrollPosition(position), 450, "top").then(() =>
        setTimeout(() => {
          updateIndexes(index);
        }, 400)
      );
    } else {
      scrollScreen(position - 99).then(() => updateIndexes(index));
    }
  };

  const handleClick = async (e, index) => {
    const card = container.current.querySelector(`[data-card='card-${index}']`);
    const cardTop = card.getBoundingClientRect().top;
    if (openCardIndex !== index) {
      setOpenCardIndex(index);
      if (isSafari()) {
        scrollIosScreen(safariScrollPosition(cardTop), 450, "top");
      } else {
        scrollScreen(cardTop - 99);
      }
      return;
    }
    openCard(cardTop, index);
  };

  return (
    <div id="packageModulesMobile">
      <SectionHeading title={content.sectionTitle} />
      <div className="tabsContainer">
        {content.tabs.map(item => (
          <p key={item.title} className={item.title === activeTab ? "active" : ""}>
            {item.title}
          </p>
        ))}
      </div>
      <div ref={container} className="cardsContainer">
        {content.packageCards.map((cardContent, index) => (
          <MobileCard
            key={index}
            index={index}
            handleClick={e => handleClick(e, index)}
            open={openCardIndex === index}
            className="card"
            content={cardContent}
          />
        ))}
        <div className="bottomLine" dangerouslySetInnerHTML={{ __html: content.bottomLine }} />
        {content.buttonText && (
          <div className="bottomButton button">
            <a href={content.buttonLink} className="linkPrimary sm">
              <div dangerouslySetInnerHTML={{ __html: content.buttonText }} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ({ content }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 992px)" });
  if (isMobile) {
    return <PackageModulesMobile content={content} />;
  } else {
    return <PackageModules content={content} />;
  }
};
